/**
 * Desglose de los costes de compra en la tarjeta «Inversión inmueble».
 *
 * Cada coste enseña su motivo (Notaría, Reforma…). Los que llegaron sin uno —el importe único
 * que tenían los inmuebles antes del desglose, que la migración guardó como «Costes de
 * adquisición»— se marcan como «Sin motivo» y se pueden nombrar AQUÍ mismo, sin abrir el
 * formulario entero: un campo con atajos, Intro para guardar y Esc para dejarlo.
 *
 * «Añadir coste» abre al final de la lista la misma fila, con motivo e importe, para sumar un
 * coste nuevo (una reforma, la tasación…) sin pasar por «Editar».
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AcquisitionCostItem, PropertyDetailResponse } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { useCan } from '../../auth/AuthContext';
import { toast } from '../../components/feedback/Toaster';
import { MoneyInput } from '../../components/form/Field';
import { Icon } from '../../components/Icon';
import { formatEurCents } from '../../lib/format';
import { COST_SUGGESTIONS, isGenericConcept } from '../../lib/acquisitionCosts';
import styles from './InvestmentCosts.module.css';

interface InvestmentCostsProps {
  property: PropertyDetailResponse;
}

export function InvestmentCosts({ property }: InvestmentCostsProps) {
  const queryClient = useQueryClient();
  const canUpdate = useCan('property:update');
  const items: AcquisitionCostItem[] = property.acquisitionCostItems ?? [];
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  // Alta de un coste nuevo: motivo e importe.
  const [adding, setAdding] = useState(false);
  const [newConcept, setNewConcept] = useState('');
  const [newAmount, setNewAmount] = useState('');

  const save = useMutation({
    mutationFn: (variables: { next: AcquisitionCostItem[]; message: string }) =>
      api.updateProperty(property.id, { acquisitionCostItems: variables.next }, property.updatedAt),
    onSuccess: (_result, variables) => {
      setEditing(null);
      setAdding(false);
      setNewConcept('');
      setNewAmount('');
      toast.success(variables.message);
      void queryClient.invalidateQueries({ queryKey: queryKeys.property(property.id) });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido guardar el coste.');
    },
  });

  function start(index: number) {
    const current = items[index]?.concept ?? '';
    setDraft(isGenericConcept(current) ? '' : current);
    setAdding(false);
    setEditing(index);
  }

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const concept = draft.trim();
    if (editing === null || !concept) return;
    save.mutate({
      next: items.map((item, index) => (index === editing ? { ...item, concept } : item)),
      message: 'Motivo guardado.',
    });
  }

  const newAmountValid = Number(newAmount) > 0;

  function startAdding() {
    setEditing(null);
    setNewConcept('');
    setNewAmount('');
    setAdding(true);
  }

  function submitNew(event?: FormEvent) {
    event?.preventDefault();
    const concept = newConcept.trim();
    if (!concept || !newAmountValid) return;
    save.mutate({
      next: [...items, { concept, amount: newAmount }],
      message: `${concept} añadido a la inversión.`,
    });
  }

  const addForm = adding ? (
    <div className={styles.editor}>
      <form className={styles.newRow} onSubmit={submitNew}>
        <input
          className={styles.input}
          value={newConcept}
          onChange={(event) => setNewConcept(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setAdding(false);
          }}
          placeholder="Motivo, por ejemplo Reforma"
          aria-label="Motivo del nuevo coste"
          maxLength={80}
          autoFocus
        />
        <div className={styles.amountField}>
          <MoneyInput
            value={newAmount}
            aria-label="Importe del nuevo coste"
            placeholder="0"
            onValueChange={setNewAmount}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setAdding(false);
            }}
          />
        </div>
        {/* Intro en cualquiera de los dos campos guarda. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
      <div className={styles.suggestions}>
        {COST_SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            className={newConcept === suggestion ? `${styles.chip} ${styles.chipOn}` : styles.chip}
            onClick={() => setNewConcept(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.cancel} onClick={() => setAdding(false)}>
          Cancelar
        </button>
        <button
          type="button"
          className={styles.save}
          onClick={() => submitNew()}
          disabled={!newConcept.trim() || !newAmountValid || save.isPending}
        >
          {save.isPending ? 'Guardando…' : 'Añadir'}
        </button>
      </div>
    </div>
  ) : null;

  const addButton =
    canUpdate && !adding ? (
      <div className={styles.addRow}>
        <button type="button" className={styles.addCost} onClick={startAdding}>
          <Icon name="plus" size={14} strokeWidth={2} />
          Añadir coste
        </button>
      </div>
    ) : null;

  if (items.length === 0) {
    return (
      <>
        {!adding ? (
          <div className={styles.row}>
            <dt>Costes de compra</dt>
            <dd className={styles.empty}>Sin registrar</dd>
          </div>
        ) : null}
        {addForm}
        {addButton}
      </>
    );
  }

  return (
    <>
      {items.map((item, index) => {
        const generic = isGenericConcept(item.concept);

        if (editing === index) {
          return (
            <div key={`editar-${index}`} className={styles.editor}>
              <form className={styles.editorRow} onSubmit={submit}>
                <input
                  className={styles.input}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setEditing(null);
                  }}
                  placeholder="¿De qué es? Por ejemplo, Reforma"
                  aria-label={`Motivo del coste de ${formatEurCents(item.amount)}`}
                  maxLength={80}
                  autoFocus
                />
                <span className={styles.amount}>{formatEurCents(item.amount)}</span>
              </form>
              <div className={styles.suggestions}>
                {COST_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className={draft === suggestion ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                    onClick={() => setDraft(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              <div className={styles.actions}>
                <button type="button" className={styles.cancel} onClick={() => setEditing(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className={styles.save}
                  onClick={() => submit()}
                  disabled={!draft.trim() || save.isPending}
                >
                  {save.isPending ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          );
        }

        return (
          <div key={`${item.concept}-${index}`} className={styles.row}>
            <dt className={styles.concept}>
              {generic ? (
                <>
                  <span className={styles.noReason}>Coste sin motivo</span>
                  {canUpdate ? (
                    <button type="button" className={styles.addReason} onClick={() => start(index)}>
                      Añadir motivo
                    </button>
                  ) : null}
                </>
              ) : canUpdate ? (
                <button
                  type="button"
                  className={styles.conceptButton}
                  onClick={() => start(index)}
                  title="Cambiar el motivo"
                >
                  {item.concept}
                </button>
              ) : (
                item.concept
              )}
            </dt>
            <dd className={styles.amount}>{formatEurCents(item.amount)}</dd>
          </div>
        );
      })}
      {addForm}
      {addButton}
    </>
  );
}
